import React, { useMemo } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { FileText, Plus, Trash2, Upload, User } from 'lucide-react';
import { getProfileAssetUrl } from '../../utils/api';
import {
  DOCUMENT_TYPE_LABELS,
  validateDocumentFile,
  validatePhotoFile,
} from '../../utils/studentAdmissionUploads';

function FilePickerRow({
  label,
  accept,
  file,
  existingName,
  existingUrl,
  onChange,
  onClear,
  readOnly,
  hint,
}) {
  if (readOnly) {
    if (!existingName && !existingUrl) return null;
    return (
      <div className="space-y-1">
        <Label className="text-muted-foreground">{label}</Label>
        {existingUrl ? (
          <a href={existingUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
            <FileText className="w-4 h-4" />
            {existingName || 'View file'}
          </a>
        ) : (
          <p className="text-sm text-foreground">{existingName}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <Label className="text-muted-foreground">{label}</Label>
      {existingName && !file && (
        <p className="text-xs text-muted-foreground">Current: {existingName}</p>
      )}
      {file && (
        <p className="text-xs text-green-600 dark:text-green-400">Selected: {file.name}</p>
      )}
      <div className="flex flex-wrap gap-2">
        <label className="inline-flex">
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => {
              const picked = e.target.files?.[0] || null;
              e.target.value = '';
              onChange(picked);
            }}
          />
          <Button type="button" variant="outline" size="sm" className="border-border" asChild>
            <span><Upload className="w-4 h-4 mr-1" /> Choose file</span>
          </Button>
        </label>
        {(file || existingName) && onClear && (
          <Button type="button" variant="ghost" size="sm" onClick={onClear}>Clear</Button>
        )}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function PhotoPickerRow({ label, file, previewUrl, initials, onChange, readOnly }) {
  const localPreview = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    return null;
  }, [file]);

  const displayUrl = localPreview || previewUrl;

  if (readOnly) {
    if (!displayUrl) return null;
    return (
      <div className="space-y-2">
        <Label className="text-muted-foreground">{label}</Label>
        <Avatar className="h-20 w-20 border border-border">
          <AvatarImage src={displayUrl} alt={label} />
          <AvatarFallback>{initials || '?'}</AvatarFallback>
        </Avatar>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label className="text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-4">
        <Avatar className="h-20 w-20 border border-border">
          {displayUrl ? <AvatarImage src={displayUrl} alt={label} /> : null}
          <AvatarFallback>{initials || <User className="w-8 h-8" />}</AvatarFallback>
        </Avatar>
        <label className="inline-flex">
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            onChange={(e) => {
              const picked = e.target.files?.[0] || null;
              e.target.value = '';
              if (picked) {
                const err = validatePhotoFile(picked);
                if (err) return;
              }
              onChange(picked);
            }}
          />
          <Button type="button" variant="outline" size="sm" className="border-border" asChild>
            <span><Upload className="w-4 h-4 mr-1" /> Upload photo</span>
          </Button>
        </label>
      </div>
    </div>
  );
}

export default function StudentAdmissionSections({
  values = {},
  onChange,
  pendingUploads,
  onPendingUploadsChange,
  existingDocuments = [],
  studentPhotoUrl = null,
  guardianPhotoUrl = null,
  studentInitials = '?',
  readOnly = false,
  showAddress = true,
  showStudentSection = true,
  showGuardianSection = true,
  onDeleteDocument,
  deletingDocId = null,
}) {
  const setField = (field, value) => {
    onChange?.((prev) => ({ ...prev, [field]: value }));
  };

  const setPending = (patch) => {
    onPendingUploadsChange?.((prev) => ({ ...prev, ...patch }));
  };

  const docByType = (type) => existingDocuments.find((d) => d.document_type === type);
  const otherDocs = existingDocuments.filter((d) => d.document_type === 'other');

  const handleDocPick = (field, file) => {
    if (!file) {
      setPending({ [field]: null });
      return;
    }
    const err = validateDocumentFile(file);
    if (err) {
      onChange?.((prev) => prev);
      return err;
    }
    setPending({ [field]: file });
    return null;
  };

  const addOtherRow = () => {
    const key = `other-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    onPendingUploadsChange?.((prev) => ({
      ...prev,
      otherDocuments: [...(prev.otherDocuments || []), { key, label: '', file: null }],
    }));
  };

  const updateOtherRow = (key, patch) => {
    onPendingUploadsChange?.((prev) => ({
      ...prev,
      otherDocuments: (prev.otherDocuments || []).map((row) => (
        row.key === key ? { ...row, ...patch } : row
      )),
    }));
  };

  const removeOtherRow = (key) => {
    onPendingUploadsChange?.((prev) => ({
      ...prev,
      otherDocuments: (prev.otherDocuments || []).filter((row) => row.key !== key),
    }));
  };

  return (
    <div className="space-y-4">
      {showStudentSection && (
        <div className="border-t border-border pt-4 space-y-4">
          <div>
            <p className="text-sm font-medium text-foreground">Student details</p>
            <p className="text-xs text-muted-foreground">Aadhar, photo, and admission documents.</p>
          </div>

          {readOnly ? (
            values.aadhar_number ? (
              <DetailLine label="Aadhar number" value={values.aadhar_number} />
            ) : null
          ) : (
            <div>
              <Label>Aadhar number</Label>
              <Input
                value={values.aadhar_number || ''}
                onChange={(e) => setField('aadhar_number', e.target.value.replace(/\D/g, '').slice(0, 12))}
                className="bg-muted border-border"
                placeholder="12-digit Aadhar"
                inputMode="numeric"
              />
            </div>
          )}

          <PhotoPickerRow
            label="Student photo"
            file={pendingUploads?.studentPhoto}
            previewUrl={studentPhotoUrl}
            initials={studentInitials}
            readOnly={readOnly}
            onChange={(file) => setPending({ studentPhoto: file })}
          />

          <FilePickerRow
            label="Aadhar document"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            file={pendingUploads?.studentAadharDoc}
            existingName={docByType('student_aadhar')?.original_filename}
            existingUrl={docByType('student_aadhar')?.asset_url}
            readOnly={readOnly}
            hint="PDF or image, max 5 MB"
            onChange={(file) => handleDocPick('studentAadharDoc', file)}
            onClear={() => setPending({ studentAadharDoc: null })}
          />
          {!readOnly && docByType('student_aadhar') && onDeleteDocument && (
            <DeleteDocButton doc={docByType('student_aadhar')} onDelete={onDeleteDocument} deleting={deletingDocId} />
          )}

          <FilePickerRow
            label="Transfer Certificate"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            file={pendingUploads?.transferCertificate}
            existingName={docByType('transfer_certificate')?.original_filename}
            existingUrl={docByType('transfer_certificate')?.asset_url}
            readOnly={readOnly}
            hint="PDF or image, max 5 MB"
            onChange={(file) => handleDocPick('transferCertificate', file)}
            onClear={() => setPending({ transferCertificate: null })}
          />
          {!readOnly && docByType('transfer_certificate') && onDeleteDocument && (
            <DeleteDocButton doc={docByType('transfer_certificate')} onDelete={onDeleteDocument} deleting={deletingDocId} />
          )}

          <div className="space-y-2">
            <Label className="text-muted-foreground">Other documents</Label>
            {readOnly ? (
              otherDocs.length === 0 ? (
                <p className="text-sm text-muted-foreground">—</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {otherDocs.map((doc) => (
                    <li key={doc.id}>
                      <a href={doc.asset_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {doc.label || doc.original_filename || DOCUMENT_TYPE_LABELS.other}
                      </a>
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <>
                {(pendingUploads?.otherDocuments || []).map((row) => (
                  <div key={row.key} className="flex flex-col sm:flex-row gap-2 items-start sm:items-end border border-border rounded-md p-3">
                    <div className="flex-1 w-full space-y-1">
                      <Label className="text-xs">Label</Label>
                      <Input
                        value={row.label}
                        onChange={(e) => updateOtherRow(row.key, { label: e.target.value })}
                        className="bg-muted border-border"
                        placeholder="Document name"
                      />
                    </div>
                    <div className="flex-1 w-full">
                      <FilePickerRow
                        label="File"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        file={row.file}
                        onChange={(file) => updateOtherRow(row.key, { file })}
                      />
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeOtherRow(row.key)} aria-label="Remove">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {otherDocs.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between text-sm border border-border rounded-md px-3 py-2">
                    <a href={doc.asset_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">
                      {doc.label || doc.original_filename}
                    </a>
                    {onDeleteDocument && (
                      <Button type="button" variant="ghost" size="sm" disabled={deletingDocId === doc.id} onClick={() => onDeleteDocument(doc.id)}>
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="border-border" onClick={addOtherRow}>
                  <Plus className="w-4 h-4 mr-1" /> Add document
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {showGuardianSection && (
        <div className="border-t border-border pt-4 space-y-4">
          <div>
            <p className="text-sm font-medium text-foreground">Guardian details</p>
            <p className="text-xs text-muted-foreground">Guardian Aadhar and photo.</p>
          </div>

          {readOnly ? (
            values.guardian_aadhar_number ? (
              <DetailLine label="Guardian Aadhar" value={values.guardian_aadhar_number} />
            ) : null
          ) : (
            <div>
              <Label>Guardian Aadhar number</Label>
              <Input
                value={values.guardian_aadhar_number || ''}
                onChange={(e) => setField('guardian_aadhar_number', e.target.value.replace(/\D/g, '').slice(0, 12))}
                className="bg-muted border-border"
                placeholder="12-digit Aadhar"
                inputMode="numeric"
              />
            </div>
          )}

          <PhotoPickerRow
            label="Guardian photo"
            file={pendingUploads?.guardianPhoto}
            previewUrl={guardianPhotoUrl ? getProfileAssetUrl(guardianPhotoUrl) : null}
            initials="G"
            readOnly={readOnly}
            onChange={(file) => setPending({ guardianPhoto: file })}
          />

          <FilePickerRow
            label="Guardian Aadhar document"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            file={pendingUploads?.guardianAadharDoc}
            existingName={docByType('guardian_aadhar')?.original_filename}
            existingUrl={docByType('guardian_aadhar')?.asset_url}
            readOnly={readOnly}
            hint="PDF or image, max 5 MB"
            onChange={(file) => handleDocPick('guardianAadharDoc', file)}
            onClear={() => setPending({ guardianAadharDoc: null })}
          />
          {!readOnly && docByType('guardian_aadhar') && onDeleteDocument && (
            <DeleteDocButton doc={docByType('guardian_aadhar')} onDelete={onDeleteDocument} deleting={deletingDocId} />
          )}
        </div>
      )}

      {showAddress && (
        <div className="border-t border-border pt-4 space-y-4">
          <div>
            <p className="text-sm font-medium text-foreground">Address</p>
          </div>
          {readOnly ? (
            (values.address || values.city || values.state || values.pincode) ? (
              <p className="text-sm text-foreground">
                {[values.address, values.city, values.state, values.pincode].filter(Boolean).join(', ')}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">—</p>
            )
          ) : (
            <>
              <div>
                <Label>Street address</Label>
                <Input
                  value={values.address || ''}
                  onChange={(e) => setField('address', e.target.value)}
                  className="bg-muted border-border"
                  placeholder="Street address"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>City</Label>
                  <Input value={values.city || ''} onChange={(e) => setField('city', e.target.value)} className="bg-muted border-border" />
                </div>
                <div>
                  <Label>State</Label>
                  <Input value={values.state || ''} onChange={(e) => setField('state', e.target.value)} className="bg-muted border-border" />
                </div>
                <div>
                  <Label>Pincode</Label>
                  <Input value={values.pincode || ''} onChange={(e) => setField('pincode', e.target.value)} className="bg-muted border-border" />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DetailLine({ label, value }) {
  return (
    <div className="text-sm">
      <span className="text-muted-foreground">{label}: </span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function DeleteDocButton({ doc, onDelete, deleting }) {
  return (
    <Button type="button" variant="ghost" size="sm" className="text-destructive" disabled={deleting === doc.id} onClick={() => onDelete(doc.id)}>
      Remove saved {DOCUMENT_TYPE_LABELS[doc.document_type] || 'document'}
    </Button>
  );
}
